import { useEffect, useState } from "react";
import { useRootStore } from "../hooks/useRootStore";
import { Box, Button, Checkbox, Table, TableCell, TableRow, Tooltip, } from "@mui/material";
import moment from 'moment';
import './Schedule.css'
import type { Schedule } from "../data/Schedule";
import { ScheduleDayType } from "../data/ScheduleDayType";
import { WeekDayRusShort } from "../data/WeekDay";
import RestorePageIcon from '@mui/icons-material/RestorePage';
import ChangeScheduleTemplateDialog from "./ChangeScheduleTemplateDialog";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";

declare global {
    interface Date {
        getWeek(): number;
    }
}
Date.prototype.getWeek = function () {
    const firstDayOfYear = new Date(this.getFullYear(), 0, 1);
    const pastDaysOfYear = (this.getTime() - firstDayOfYear.getTime()) / 86400000;
    return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay()) / 7);
}

export default function Schedule({ employeeAgentID }: { employeeAgentID: number }) {
    const { api, userProfile, scheduleChannel } = useRootStore();

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const [loading, setLoading] = useState(true);
    const [schedule, setSchedule] = useState([] as Schedule[]);
    const [templateChanging, setTemplateChanging] = useState(false);
    const [vacationRequesting, setVacationRequesting] = useState(false);
    const [startDate, setStartDate] = useState(today);
    const [endDate, setEndDate] = useState(moment(today).add(1, 'M').toDate());
    const [selectedDays, setSelectedDays] = useState(new Set([] as number[]));
    const [vacationBalance, setVacationBalance] = useState(0);
    const [vacationBalanceLoaded, setVacationBalanceLoaded] = useState(false);

    useEffect(() => {
        api.getSchedule(employeeAgentID, startDate, moment(endDate).add(1, 'd').toDate()).then(e => {
            setSchedule(e);
            setLoading(false);
        });

    }, [employeeAgentID, api, startDate, endDate]);

    useEffect(() => {
        const unsubscribeOnScheduleChanged = scheduleChannel.on('onScheduleChanged', (employeeID: number) => {
            if (employeeID == employeeAgentID) {
                setLoading(true);
                api.getSchedule(employeeAgentID, startDate, moment(endDate).add(1, 'd').toDate()).then(e => {
                    setSchedule(e);
                    setLoading(false);
                });
            }
        })
        return () => unsubscribeOnScheduleChanged();
    }, [api, employeeAgentID, scheduleChannel])

    const handleHourClick = async (dayIndex: number, hourIndex: number) => {
        if (new Date(schedule[dayIndex].Date).getTime() <= today.getTime() || !me) {
            return false;
        }
        const newSchedule = schedule.slice();
        newSchedule[dayIndex].Hours[hourIndex] = !newSchedule[dayIndex].Hours[hourIndex];
        api.setSchedule(newSchedule[dayIndex].Date, newSchedule[dayIndex].Hours)
            .then(() => setSchedule(newSchedule));
    }

    const grouped =
        Object.groupBy(schedule, x => new Date(x.Date).getFullYear() * 100 + new Date(x.Date).getWeek());

    const a = Object.entries(grouped).map(x => {
        return { weekNum: x[0], schedule: x[1] as Schedule[] };
    });

    const handleDaySelectCheckClick = (e: React.ChangeEvent<HTMLInputElement>, dayIndex: number) => {
        //setChecked([event.target.checked, event.target.checked]);
        if (e.target.checked) {            
            selectedDays.add(dayIndex)
            setSelectedDays(new Set(selectedDays));
        } else
        {
            selectedDays.delete(dayIndex)
            setSelectedDays(new Set(selectedDays));
        }
    };

    const fillDayByTemplate = (dayIndex: number) => {
        let dow = (new Date(schedule[dayIndex].Date)).getDay();
        dow = (dow == 0 ? 7 : dow);
        api.getScheduleTemplate(userProfile!.ID).then(template => {
            const templateDay = template[dow - 1];
            const newSchedule = schedule.slice();
            templateDay.hours.forEach((h, i) => {
                newSchedule[dayIndex].Hours[i] = h;
            })
            
            api.setSchedule(newSchedule[dayIndex].Date, newSchedule[dayIndex].Hours)
                .then(() => setSchedule(newSchedule));
        })
    }

    const fillScheduleByTemplate = (fromDate: Date, toDate: Date) => {
        api.fillEmployeeScheduleByTemplate(fromDate, toDate);
    }

    const requestVacation = () => {
        const dates = [] as Date[];
        selectedDays.forEach((i) => {
            dates.push(schedule[i].Date);
        });
        api.requestVacation(dates).then(() => {
            setVacationRequesting(false);
            selectedDays.forEach((i) => {
                schedule[i].DayType = ScheduleDayType.requestedVacation;
            });
            setSelectedDays(new Set([] as number[]));
        });
    }

    const resetVacationRequesting = () => {
        setSelectedDays(new Set([] as number[]));
        setVacationRequesting(false);
    }

    const toggleVacationRequesting = () => {
        if (vacationRequesting) {
            resetVacationRequesting();
        } else {
            setVacationRequesting(true);
            api.getMyVacationBalance().then(vb => {
                setVacationBalance(vb);
                setVacationBalanceLoaded(true);
            });
        }
    }
    
    const me = employeeAgentID == userProfile?.ID;
    let di = -1;
    return (
        loading ? 'Загрузка' :
            <Box sx={{ display: 'flex' }}>
                {templateChanging ? <ChangeScheduleTemplateDialog onSave={() => setTemplateChanging(false)} onCancel={() => setTemplateChanging(false)} /> : ''}
                <Box className='schedule' style={{ position: 'relative' }}>
                    {!vacationRequesting ? '' :
                        <Box className='request-vacation-dialog'>
                            <Box className='title'>Запрос отпуска</Box>
                            <Box className='content'>
                                <Table>
                                    <TableRow>
                                        <TableCell sx={{ p: 1, border: 'none !important' }}>Накоплено: </TableCell><TableCell sx={{ p: 1, border: 'none !important' }}>{vacationBalanceLoaded ? `${vacationBalance} дней` : 'подсчет...'}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell sx={{ p: 1, border: 'none !important' }}>Выбрано: </TableCell><TableCell sx={{ p: 1, border: 'none !important' }}>{selectedDays.size} дней</TableCell>
                                    </TableRow>
                                </Table>
                            </Box>
                            <Box className='btns'>
                                <Button onClick={requestVacation}>Запросить</Button>
                                <Button onClick={resetVacationRequesting}>Отмена</Button>
                            </Box>
                        </Box>
                    }
                    <Box className='schedule-header'>
                        <Box>
                            <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale="ru" >
                                <DatePicker format="DD.MM.YYYY"
                                    defaultValue={moment(startDate)}
                                    onChange={(e) => { if (e) setStartDate(e.toDate()) }}                                    
                                    sx={{ width: 200, mr: 2 }}
                                    label="С даты"
                                />
                                <DatePicker format="DD.MM.YYYY"
                                    defaultValue={moment(endDate)}
                                    onChange={(e) => { if (e) setEndDate(e.toDate()) }}
                                    sx={{
                                        width: 200
                                    }}
                                    label="По дату"
                                />
                            </LocalizationProvider>
                        </Box>
                        {me ?
                            <Box className='header-btns'>
                                <Button onClick={() => setTemplateChanging(true)}>Изменить шаблон</Button>
                                <Button onClick={toggleVacationRequesting}>{vacationRequesting ? "Отмена запроса" : "Запрос отпуска"}</Button>
                            </Box>
                            : ''
                        }
                    </Box>
                    <Box>
                        {a.map((w, i) =>
                            <Box className="week" key={w.weekNum}>
                                <Box className="week-num">
                                    {String(w.weekNum).substring(4)}
                                </Box>
                                <Box sx={{flex:1}}>
                                    {w.schedule.map(d => {
                                        const dd = ++di;
                                        return (
                                            <Box className={'working-day' + ((new Date(d.Date).getTime() <= today.getTime()) || !me ? ' disabled' : '')} key={dd}>
                                                <span className={"working-date" + (d.DayType & ScheduleDayType.weekend ? ' weekend' : '')}>{moment(d.Date).format("DD.MM.YY")}</span>
                                                <span className={"week-day" + (d.DayType & ScheduleDayType.weekend ? ' weekend' : '')}>{WeekDayRusShort[new Date(d.Date).getDay()]}</span>
                                                {!vacationRequesting ? '' :
                                                    <span className='day-select'>
                                                        {(d.DayType & ScheduleDayType.vacation || d.DayType & ScheduleDayType.requestedVacation)
                                                            || (new Date(d.Date).getTime() <= today.getTime()) ? ''
                                                            : <Checkbox sx={{ p: 0, m: 0 }} onChange={(e) => handleDaySelectCheckClick(e, dd)} />}
                                                    </span>
                                                }
                                                {
                                                    d.DayType & ScheduleDayType.vacation
                                                        ? <span className="vacation">Отпуск</span>
                                                        : d.DayType & ScheduleDayType.requestedVacation
                                                            ? <span className="requested-vacation"> Запрос отпуска</span>
                                                            :
                                                            <Box style={{ display: 'flex', flex: 1 }}>
                                                                {d.Hours.map((h, hi) =>
                                                                    <span key={hi}
                                                                        className={"working-hour" + (h ? ' work' : '')}
                                                                        onClick={() => handleHourClick(dd, hi)}
                                                                    >
                                                                        {hi}
                                                                    </span>
                                                                )}
                                                            </Box>
                                                }
                                                <span className='use-template'>
                                                    {((d.DayType & ScheduleDayType.requestedVacation |
                                                        d.DayType & ScheduleDayType.requestedVacation) ||
                                                        ((new Date(d.Date)).getTime() <= today.getTime()) ||
                                                        !me) ? ''
                                                        :
                                                        <Tooltip title='Заполнить день по шаблону' placement="left">
                                                            <RestorePageIcon color='info' onClick={() => fillDayByTemplate(dd)} />
                                                        </Tooltip>
                                                    }
                                                </span>
                                            </Box>);
                                    })}
                                </Box>
                                <Box sx={{ position: 'relative' }}>
                                    {/*w.schedule.filter(x => (new Date(x.Date)).getTime() > today.getTime()).length == 0 ||*/ !me ? '' :
                                        <Box className='use-template-week'>
                                            <Tooltip title='Заполнить неделю по шаблону' placement="right">
                                                {w.schedule.find(x => (new Date(x.Date)).getTime() > today.getTime()) !== undefined
                                                    ? <RestorePageIcon color='info' onClick={() => fillScheduleByTemplate(a[i].schedule[0].Date, a[i].schedule[a[i].schedule.length - 1].Date)} />
                                                    : <>&nbsp;</>
                                                }
                                            </Tooltip>
                                        </Box>
                                    }
                                </Box>
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>
    );
}